import { hostAppCall } from "./appFunctions.js"
import { fetchGetJson, handleError } from "./fetch-json.js"
import type { CachedUser, UserError, MaybeUser, UserSessionExpired, User } from "#/types.js"
import { clearAuthUser, getCachedMe, getSessionId, setCachedMe, setSessionId } from "./auth-storage.js"

const cachedMeTTL:number = 30 * 60 * 1000 // 30 minutes.

let promiseOfAuthRequest: Promise<User> | undefined

export function getAuthUserWithHandlingError():Promise<MaybeUser> {
    return getAuthUser().then( (maybeMe: MaybeUser) => {
        if (maybeMeIsError(maybeMe)) handleError(maybeMe.error) // TODO: clean up this
        return maybeMe
   })
}

export async function getAuthUser():Promise<MaybeUser> {
    try{
        const cachedMe: CachedUser| null = getCachedMe()
        const sessionIdCookie = getSessionId()
        if (!sessionIdCookie && (!cachedMe || maybeMeSessionExpired(cachedMe.user))) {
            return { isDefined: false, status: "sessionExpired" }
        }
        else if (cachedMe && Date.now() - cachedMe.issuedAt < cachedMeTTL && sessionIdCookie == cachedMe.user.sessionId) return cachedMe.user

        if (!promiseOfAuthRequest) promiseOfAuthRequest = requestAuthUser().finally(() => promiseOfAuthRequest = undefined)
        const authResult = await promiseOfAuthRequest
        if (authResult) {
            if (maybeMeSessionExpired(authResult)) {
                return {isDefined: false, status: authResult.status}
            }
            return authResult;
        }
        return { isDefined: false, type: "error",  error: authResult };
    } catch (e) {
        return { isDefined: false, type: "error", error: e as Response }
    }
}

function requestAuthUser(): Promise<User> {
    return fetchGetJson<User>("/v1/me")
    .then((userData: User) => {
        if (userData.sessionId) refreshSessionId(userData.sessionId)
        setCachedMe(JSON.stringify({ user: userData, issuedAt: Date.now() }))
        return userData
    }).catch((e: Response) => {
        clearAuthUser()
        throw e
    })
}


// If the expiration date is less than 3 months, the session id will be reissued.
async function refreshSessionId(sessionId:string): Promise<void> {
    setSessionId(sessionId)
    hostAppCall("updateSessionId", sessionId) // assume that older versions (495 and earlier) of the app cannot update the reissued session id. Remove this comment after 2021.12.31.
}

export const maybeMeIsMe = (maybeMe: MaybeUser): maybeMe is User => maybeMe.isDefined
export const maybeMeIsError = (maybeMe: MaybeUser): maybeMe is UserError => !maybeMe.isDefined && (maybeMe as UserError)?.type == "error"
export const maybeMeSessionExpired = (maybeMe: MaybeUser): maybeMe is UserSessionExpired => !maybeMe.isDefined && (maybeMe as UserSessionExpired)?.status == "sessionExpired"